import React, { useMemo, useState } from 'react';
import AdminLayout from 'cms/core/shared/AdminLayout';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { HttpService } from 'core/services/http';
import { useSnackbar } from 'notistack';
import { 
    Box, 
    Button, 
    Card, 
    CardContent, 
    Grid, 
    MenuItem, 
    Select, 
    TextField, 
    Typography, 
    Chip, 
    Alert,
    Divider,
    Stack,
    Paper,
    Avatar,
    IconButton
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import WorkIcon from '@mui/icons-material/Work';
import DescriptionIcon from '@mui/icons-material/Description';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DownloadIcon from '@mui/icons-material/Download';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import BusinessIcon from '@mui/icons-material/Business';
import FilePresentIcon from '@mui/icons-material/FilePresent';
import { CVHttpService } from 'features/feature_cv/services';

const decisionOptions = [
  { value: '', label: 'Pending' },
  { value: 'shortlisted', label: 'Shortlisted' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'hired', label: 'Hired' },
];

const ApplicationDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [decision, setDecision] = useState('');
  const [notes, setNotes] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);

  const { data, isLoading, isError, refetch } = useQuery(['cms_application', id], async () => {
    const service = new HttpService();
    return await service.get(`/job/apply/${id}`, null);
  }, {
    enabled: !!id,
    onSuccess: (app) => {
      setDecision(app?.final_decision || '');
      setNotes(app?.decision_notes || '');
    }
  });

  const application = useMemo(() => data ?? {}, [data]);
  
  const cv = application?.cv;
  const cvFile = application?.cv_file;


  
  // Get CV file download URL
  const getCVFileUrl = () => {
    if (!cvFile?.file) return null;
    if (cvFile.file.startsWith('http')) return cvFile.file;
    if (cvFile.file.startsWith('/')) {
      return HttpService.SiteBase + cvFile.file;
    }
    return HttpService.SiteBase + '/' + cvFile.file;
  };
  
  // Get CV ID for PDF download
  const cvId = cv?.id || (typeof cv === 'number' ? cv : null);
  
  const handleDownloadCVPDF = async () => {
    if (!cvId) return;
    try {
      await CVHttpService.downloadPDF(cvId, 'style2');
    } catch (err) {
      enqueueSnackbar('Failed to download CV PDF', { variant: 'error' });
    }
  };

  const { mutate: save, isLoading: saving } = useMutation(async (payload) => {
    const service = new HttpService();
    return await service.put(`/job/apply/${id}`, payload);
  }, {
    onSuccess: async (response) => {
      setSaveSuccess(true);
      enqueueSnackbar('Decision saved successfully', { variant: 'success' });
      // Update local state immediately
      if (response) {
        setDecision(response?.final_decision || '');
        setNotes(response?.decision_notes || '');
      }
      // Refetch to get latest data
      await refetch();
      queryClient.invalidateQueries(['cms_application', id]);
      queryClient.invalidateQueries(['cms_applications']);
      // Hide success message after 3 seconds
      setTimeout(() => setSaveSuccess(false), 3000);
    },
    onError: (error) => {
      enqueueSnackbar(error?.message || 'Failed to save decision', { variant: 'error' });
    }
  });

  const handleSave = () => {
    // Get job ID - handle both object and ID number cases
    const jobId = application?.job?.id || (typeof application?.job === 'number' ? application.job : null);
    
    const payload = {
      job: jobId,
      final_decision: decision || null,
      decision_notes: notes || null
    };
    save(payload);
  };

  const getDecisionLabel = (value) => {
    const option = decisionOptions.find(opt => opt.value === value);
    return option ? option.label : 'Pending';
  };

  const getDecisionColor = (value) => {
    switch (value) {
      case 'shortlisted':
        return 'info';
      case 'hired':
        return 'success';
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <AdminLayout pageTitle={`Application #${id}`}>
      <Box sx={{ p: 3 }}>
        <Button 
          variant="text" 
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)} 
          sx={{ mb: 3 }}
        >
          Back to Applications
        </Button>

        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <Typography>Loading application details...</Typography>
          </Box>
        )}
        
        {isError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Failed to load application. Please try again.
          </Alert>
        )}

        {!isLoading && !isError && (
          <Grid container spacing={3}>
            {/* Main Content */}
            <Grid item xs={12} md={8}>
              <Stack spacing={3}>
                {/* Applicant Information Card */}
                <Card elevation={2}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                      <Avatar sx={{ width: 64, height: 64, bgcolor: 'primary.main' }}>
                        <PersonIcon sx={{ fontSize: 32 }} />
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h5" fontWeight={700} gutterBottom color="text.primary">
                          {`${application?.user?.profile?.first_name ?? ''} ${application?.user?.profile?.last_name ?? ''}`.trim() || application?.user?.username || 'Unknown Applicant'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {application?.user?.email || '-'}
                        </Typography>
                      </Box>
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <EmailIcon fontSize="small" color="action" />
                          <Typography variant="caption" color="text.secondary">Email</Typography>
                        </Box>
                        <Typography variant="body1" fontWeight={500}>
                          {application?.user?.email || '-'}
                        </Typography>
                      </Grid>
                      {application?.user?.profile?.phone_number && (
                        <Grid item xs={12} sm={6}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <PhoneIcon fontSize="small" color="action" />
                            <Typography variant="caption" color="text.secondary">Phone</Typography>
                          </Box>
                          <Typography variant="body1" fontWeight={500}>
                            {application?.user?.profile?.phone_number}
                          </Typography>
                        </Grid>
                      )}
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <CalendarTodayIcon fontSize="small" color="action" />
                          <Typography variant="caption" color="text.secondary">Submitted</Typography>
                        </Box>
                        <Typography variant="body1" fontWeight={500}>
                          {application?.created_at ? new Date(application.created_at).toLocaleString() : '-'}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                {/* Job Information Card */}
                <Card elevation={2}>
                  <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <WorkIcon sx={{ color: 'text.primary' }} />
                        <Typography variant="h6" fontWeight={700} color="text.primary">
                          Job Applied For
                        </Typography>
                      </Box>
                    <Divider sx={{ mb: 2 }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <BusinessIcon fontSize="small" color="action" />
                      <Typography variant="body1" fontWeight={600}>
                        {application?.job?.title ?? '-'}
                      </Typography>
                    </Box>
                    {application?.job?.company?.title && (
                      <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                        {application.job.company.title}
                      </Typography>
                    )}
                    {application?.job?.id && (
                      <Box sx={{ mt: 2 }}>
                        <Button
                          component={Link}
                          to={`/cms/job/${application.job.id}`}
                          variant="outlined"
                          size="small"
                        >
                          View Job Details
                        </Button>
                      </Box>
                    )}
                  </CardContent>
                </Card>

                {/* Cover Letter Card */}
                {application?.cover_letter && (
                  <Card elevation={2}>
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <DescriptionIcon sx={{ color: 'text.primary' }} />
                        <Typography variant="h6" fontWeight={700} color="text.primary">
                          Cover Letter
                        </Typography>
                      </Box>
                      <Divider sx={{ mb: 2 }} />
                      <Typography 
                        variant="body1" 
                        sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}
                        color="text.primary"
                      >
                        {application.cover_letter}
                      </Typography>
                    </CardContent>
                  </Card>
                )}

                {/* CV File Card */}
                {cvFile && (
                  <Card elevation={2}>
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <FilePresentIcon sx={{ color: 'text.primary' }} />
                        <Typography variant="h6" fontWeight={700} color="text.primary">
                          CV File
                        </Typography>
                      </Box>
                      <Divider sx={{ mb: 2 }} />
                      <Stack spacing={2}>
                        {cvFile.title && (
                          <Box>
                            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                              File Name
                            </Typography>
                            <Typography variant="body1" fontWeight={500}>
                              {cvFile.title}
                            </Typography>
                          </Box>
                        )}
                        {cvFile.description && (
                          <Box>
                            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                              Description
                            </Typography>
                            <Typography variant="body2" color="text.primary">
                              {cvFile.description}
                            </Typography>
                          </Box>
                        )}
                        {cvFile.created_at && (
                          <Box>
                            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                              Uploaded
                            </Typography>
                            <Typography variant="body2" color="text.primary">
                              {new Date(cvFile.created_at).toLocaleDateString()}
                            </Typography>
                          </Box>
                        )}
                        {getCVFileUrl() && (
                          <Box sx={{ pt: 1 }}>
                            <Button
                              variant="contained"
                              startIcon={<DownloadIcon />}
                              href={getCVFileUrl()}
                              target="_blank"
                              download
                              sx={{ mr: 1 }}
                            >
                              Download CV File
                            </Button>
                          </Box>
                        )}
                      </Stack>
                    </CardContent>
                  </Card>
                )}

                {/* CV Card (if CV exists instead of CV File) */}
                {cv && !cvFile && (
                  <Card elevation={2}>
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <PictureAsPdfIcon sx={{ color: 'text.primary' }} />
                        <Typography variant="h6" fontWeight={700} color="text.primary">
                          CV Document
                        </Typography>
                      </Box>
                      <Divider sx={{ mb: 2 }} />
                      <Stack spacing={2}>
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                            CV Title
                          </Typography>
                          <Typography variant="body1" fontWeight={500}>
                            {cv.title || cv.name || 'CV Document'}
                          </Typography>
                        </Box>
                        {cvId && (
                          <Box sx={{ pt: 1 }}>
                            <Button
                              variant="contained"
                              startIcon={<PictureAsPdfIcon />}
                              onClick={handleDownloadCVPDF}
                            >
                              Download CV as PDF
                            </Button>
                          </Box>
                        )}
                      </Stack>
                    </CardContent>
                  </Card>
                )}
              </Stack>
            </Grid>

            {/* Sidebar - Decision Panel */}
            <Grid item xs={12} md={4}>
              <Card elevation={2} sx={{ position: 'sticky', top: 20 }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h6" fontWeight={700} color="text.primary">
                      Final Decision
                    </Typography>
                    {application?.final_decision && (
                      <Chip 
                        label={getDecisionLabel(application.final_decision)} 
                        color={getDecisionColor(application.final_decision)}
                        size="small"
                      />
                    )}
                  </Box>
                  
                  {saveSuccess && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                      Decision saved successfully!
                    </Alert>
                  )}
                  
                  <Stack spacing={2}>
                    <Select 
                      size="small" 
                      value={decision} 
                      onChange={(e) => setDecision(e.target.value)}
                      displayEmpty
                      fullWidth
                    >
                      {decisionOptions.map(opt => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                      ))}
                    </Select>
                    <TextField
                      label="Decision notes"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      multiline
                      minRows={4}
                      maxRows={8}
                      size="small"
                      inputProps={{ maxLength: 500 }}
                      fullWidth
                      helperText={`${notes.length}/500 characters`}
                    />
                    <Button 
                      variant="contained" 
                      onClick={handleSave} 
                      disabled={saving}
                      fullWidth
                      size="large"
                    >
                      {saving ? 'Saving...' : 'Save Decision'}
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
      </Box>
    </AdminLayout>
  );
};

export default ApplicationDetailsPage;


